import React, { useState, useEffect } from "react";
import { GetNewUsers } from "./apis/get";
import LoaderService from "../services/LoaderService";
import {
  Users,
  CheckCircle,
  UserCheck,
  UserX,
  Search,
  Filter
} from "lucide-react";
import toast from "react-hot-toast";

const StatCard = ({ title, value, icon: Icon, colorClass }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center justify-between shadow-lg">
    <div>
      <p className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-1">{title}</p>
      <h3 className="text-white text-2xl font-black">{value}</h3>
    </div>
    <div className={`p-3 rounded-lg ${colorClass} bg-opacity-10 border border-current`}>
      <Icon size={24} className="opacity-80" />
    </div>
  </div>
);

const calculateDateRange = (preset) => {
  if (preset === "custom" || preset === "all" || preset === "today") return null;

  const today = new Date();
  const fromDate = new Date(today);
  const toDate = new Date(today);

  switch (preset) {
    case 'yesterday':
      fromDate.setDate(today.getDate() - 1);
      toDate.setDate(today.getDate() - 1); // Set toDate to yesterday as well
      break;
    case 'last2days':
      fromDate.setDate(today.getDate() - 2);
      break;
    case 'last3days':
      fromDate.setDate(today.getDate() - 3);
      break;
    case 'last7days':
      fromDate.setDate(today.getDate() - 7);
      break;
    case 'last30days':
      fromDate.setDate(today.getDate() - 30);
      break;
    default:
      break;
  }

  const formatDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // For the API, we need to include time to capture the full day
  const formatDateTimeStart = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00`;
  };

  const formatDateTimeEnd = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T23:59:59`;
  };

  // For yesterday specifically, we want to be more precise
  if (preset === 'yesterday') {
    console.log('Yesterday selected - fromDate:', fromDate, 'toDate should be same as fromDate');
    return { 
      from: formatDateTimeStart(fromDate), 
      to: formatDateTimeEnd(fromDate) // Use fromDate for both (yesterday only)
    };
  }

  console.log('Other preset selected:', preset, 'fromDate:', fromDate, 'toDate:', toDate);
  return { 
    from: formatDateTimeStart(fromDate), 
    to: formatDateTimeEnd(toDate) 
  };
};

const NewUsers = () => {
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Stats calculated from current page
  const [stats, setStats] = useState({
    progress100: 0,
    registered: 0,
    guest: 0,
    android: 0,
    ios: 0,
    web: 0
  });
  const [currentPage, setCurrentPage] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    date_preset: "today",
    from_date: "",
    to_date: "",
    user_type: "all",
  });

  const fetchData = async (overrideFilters = null, pageIndex = 0) => {
    const activeFilters = overrideFilters || filters;

    try {
      setLoading(true);
      LoaderService.show();
      
      const PAGE_SIZE = 100;
      const apiParams = {
        limit: PAGE_SIZE,
        offset: pageIndex * PAGE_SIZE,
      };
      
      if (activeFilters.date_preset === "custom") {
        if (activeFilters.from_date) apiParams.from_date = `${activeFilters.from_date}T00:00:00`;
        if (activeFilters.to_date) apiParams.to_date = `${activeFilters.to_date}T23:59:59`;
      } else if (activeFilters.date_preset !== "all") {
        const range = calculateDateRange(activeFilters.date_preset);
        if (range) {
          apiParams.from_date = range.from;
          apiParams.to_date = range.to;
        }
      }

      // Handle user type filter - send "registered" to API for all registered variants
      if (activeFilters.user_type === "guest") {
        apiParams.user_type = "guest";
      } else if (activeFilters.user_type === "registered" || activeFilters.user_type === "registered_100" || activeFilters.user_type === "registered_not_100") {
        apiParams.user_type = "registered";
      }

      console.log('API Params being sent:', apiParams);
      const response = await GetNewUsers(apiParams);
      
      if (response && response.status === "success") {
        let fetchedUsers = response.data?.users || [];
        
        // Apply frontend filtering for registered user progress
        if (activeFilters.user_type === "registered_100") {
          fetchedUsers = fetchedUsers.filter(user => user.user_type === "registered" && user.progress === 100);
        } else if (activeFilters.user_type === "registered_not_100") {
          fetchedUsers = fetchedUsers.filter(user => user.user_type === "registered" && user.progress < 100);
        }
        // For "registered" - no additional filtering needed, API already filtered
        
        const nextTotalCount = fetchedUsers.length; // Use filtered count
        setData(fetchedUsers);
        setTotalCount(nextTotalCount);

        // Calculate stats from filtered data
        const progress100Count = fetchedUsers.filter(user => user.progress === 100).length;
        const registeredCount = fetchedUsers.filter(user => user.user_type === "registered").length;
        const guestCount = fetchedUsers.filter(user => user.user_type === "guest").length;
        const androidCount = fetchedUsers.filter(user => user.platform === "android").length;
        const iosCount = fetchedUsers.filter(user => user.platform === "ios").length;
        const webCount = fetchedUsers.filter(user => user.platform === "web").length;

        setStats({
          progress100: progress100Count,
          registered: registeredCount,
          guest: guestCount,
          android: androidCount,
          ios: iosCount,
          web: webCount,
        });
      } else {
        toast.error("Failed to fetch new users data");
      }
    } catch (error) {
      console.error("Error fetching new users:", error);
      toast.error("An error occurred while fetching data");
    } finally {
      setLoading(false);
      LoaderService.hide();
    }
  };

  useEffect(() => {
    fetchData(filters, 0);
  }, []); // Initial load

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchData(filters, 0);
  };

  const clearFilters = () => {
    const resetFilters = {
      date_preset: "all",
      from_date: "",
      to_date: "",
      user_type: "all"
    };
    setFilters(resetFilters);
    setCurrentPage(0);
    fetchData(resetFilters, 0);
  };

  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(totalCount / 100);
    if (newPage < 0 || newPage >= totalPages) return;
    setCurrentPage(newPage);
    fetchData(filters, newPage);
  };

  const PAGE_SIZE = 100;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const showingStart = totalCount === 0 ? 0 : currentPage * PAGE_SIZE + 1;
  const showingEnd = Math.min(totalCount, (currentPage + 1) * PAGE_SIZE);

  return (
    <div className="p-6 bg-slate-950 min-h-screen">
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-white uppercase tracking-wider">New Users</h1>
          <p className="text-slate-400 text-sm mt-1">Overview of newly registered and guest users</p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard 
          title="Total Users" 
          value={totalCount} 
          icon={Users} 
          colorClass="text-blue-500" 
        />
        <StatCard 
          title="100% Progress" 
          value={stats.progress100} 
          icon={CheckCircle} 
          colorClass="text-emerald-500" 
        />
        <StatCard 
          title="Registered Users" 
          value={stats.registered} 
          icon={UserCheck} 
          colorClass="text-purple-500" 
        />
        <StatCard 
          title="Guest Users" 
          value={stats.guest} 
          icon={UserX} 
          colorClass="text-amber-500" 
        />
        <StatCard 
          title="Android Users" 
          value={stats.android} 
          icon={Users} 
          colorClass="text-green-500" 
        />
        <StatCard 
          title="iOS Users" 
          value={stats.ios} 
          icon={Users} 
          colorClass="text-gray-500" 
        />
        <StatCard 
          title="Web Users" 
          value={stats.web} 
          icon={Users} 
          colorClass="text-cyan-500" 
        />
      </div>

      {/* Filters */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-8 shadow-lg">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={18} className="text-slate-400" />
          <h2 className="text-white font-bold tracking-wide">Filter Users</h2>
        </div>
        <form onSubmit={handleFilterSubmit} className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1.5 min-w-[150px]">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Date Filter</label>
            <select 
              value={filters.date_preset}
              onChange={(e) => setFilters({...filters, date_preset: e.target.value})}
              className="bg-slate-950 border border-slate-800 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 transition-colors appearance-none"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="last2days">Last 2 Days</option>
              <option value="last3days">Last 3 Days</option>
              <option value="last7days">Last 7 Days</option>
              <option value="last30days">Last 30 Days</option>
              <option value="custom">Custom Date Range</option>
              <option value="all">All Time</option>
            </select>
          </div>

          {filters.date_preset === "custom" && (
            <>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">From Date</label>
                <input 
                  type="date" 
                  value={filters.from_date}
                  onChange={(e) => setFilters({...filters, from_date: e.target.value})}
                  className="bg-slate-950 border border-slate-800 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">To Date</label>
                <input 
                  type="date" 
                  value={filters.to_date}
                  onChange={(e) => setFilters({...filters, to_date: e.target.value})}
                  className="bg-slate-950 border border-slate-800 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>
            </>
          )}

          <div className="flex flex-col gap-1.5 min-w-[150px]">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">User Type</label>
            <select 
              value={filters.user_type}
              onChange={(e) => setFilters({...filters, user_type: e.target.value})}
              className="bg-slate-950 border border-slate-800 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 transition-colors appearance-none"
            >
              <option value="all">All</option>
              <option value="guest">Guest</option>
              <optgroup label="Registered">
                <option value="registered"> All Registered</option>
                <option value="registered_100"> 100% Progress</option>
                <option value="registered_not_100"> &lt;100% Progress</option>
              </optgroup>
            </select>
          </div>
          <div className="flex gap-2 ml-auto">
            <button 
              type="button" 
              onClick={clearFilters}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-bold rounded-lg transition-colors"
            >
              Clear
            </button>
            <button 
              type="submit" 
              className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold rounded-lg transition-colors shadow-[0_0_15px_rgba(37,99,235,0.3)] flex items-center gap-2"
            >
              <Search size={16} />
              Search
            </button>
          </div>
        </form>
      </div>

      {/* Data Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-lg">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-950/50 border-b border-slate-800">
                <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-wider">User Info</th>
                <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-wider">Contact</th>
                <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-wider">Status</th>
                <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-wider">Profile Details</th>
                <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-wider">Location</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-slate-500">Loading data...</td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-slate-500">No users found matching the criteria.</td>
                </tr>
              ) : (
                data.map((user, index) => {
                  const p = user.matrimonial_profile;
                  
                  return (
                    <tr key={user.id || index} className="border-b border-slate-800/50 hover:bg-slate-800/20 transition-colors">
                      <td className="p-4 align-top">
                        <div className="flex flex-col gap-1">
                          <span className="text-sm font-bold text-white">{user.username || "N/A"}</span>
                          <span className="text-[10px] font-mono text-slate-500">{user.user_id}</span>
                          <span className="text-[10px] text-slate-400 mt-1">ID: {user.id}</span>
                        </div>
                      </td>
                      <td className="p-4 align-top">
                        <div className="flex flex-col gap-1">
                          <span className="text-xs text-slate-300 font-medium">{user.phone_number || "N/A"}</span>
                          <span className="text-[10px] text-slate-500">{user.email_address || "N/A"}</span>
                        </div>
                      </td>
                      <td className="p-4 align-top">
                        <div className="flex flex-col gap-2 items-start">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-tighter border ${
                            user.user_type === "registered" 
                              ? "bg-purple-500/10 text-purple-400 border-purple-500/20" 
                              : "bg-amber-500/10 text-amber-400 border-amber-500/20"
                          }`}>
                            {user.user_type || "N/A"}
                          </span>
                          
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-[10px] font-bold ${user.progress === 100 ? "text-emerald-400" : "text-blue-400"}`}>
                              {user.progress || 0}%
                            </span>
                            <div className="w-16 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${user.progress === 100 ? "bg-emerald-500" : "bg-blue-500"}`}
                                style={{ width: `${Math.min(100, user.progress || 0)}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 align-top">
                        {p ? (
                          <div className="flex flex-col gap-1">
                            <span className="text-xs text-slate-300 font-bold">{p.name || "N/A"}</span>
                            <div className="flex gap-2 text-[10px] text-slate-400 uppercase tracking-wider">
                              <span>{p.gender || "N/A"}</span>
                              <span>•</span>
                              <span>{p.marital_status || "N/A"}</span>
                            </div>
                            <span className="text-[10px] font-mono text-slate-500 mt-1">{p.matrimonial_id}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-500 italic">N/A</span>
                        )}
                      </td>
                      <td className="p-4 align-top">
                        {p ? (
                          <div className="flex flex-col gap-1">
                            <span className="text-xs text-slate-300">{p.city || "N/A"}</span>
                            <span className="text-[10px] text-slate-500">{p.state || "N/A"}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-500 italic">N/A</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-col gap-3 mt-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-400">
          Showing {showingStart} - {showingEnd} of {totalCount} users
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0 || loading}
            className={`px-4 py-2 rounded-lg text-sm font-bold ${currentPage === 0 || loading ? "bg-slate-800 text-slate-500 cursor-not-allowed" : "bg-slate-700 text-white hover:bg-slate-600"}`}
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={(currentPage + 1) >= totalPages || loading}
            className={`px-4 py-2 rounded-lg text-sm font-bold ${currentPage + 1 >= totalPages || loading ? "bg-slate-800 text-slate-500 cursor-not-allowed" : "bg-slate-700 text-white hover:bg-slate-600"}`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewUsers;
